import { defineStore } from "pinia";
import { log } from "@/state/pinia/log";
import axios from "axios";
import { authHeader } from "@/helpers/authservice/auth-header";
import {apiRequestsHelper} from "@/state/pinia/apiRequestHelper";

const DEFAULT_MESSAGE = "Pobieranie danych...";
const DEFAULT_POLL_INTERVAL = 2000;

export const useLazyLoaderModule = defineStore("lazyloaderManagerModule", {
    state: () => ({
        // overlay / modal
        displayLazyLoader: false,
        displayProgressBar: false,

        // current tracked task
        progressBarProgress: 0,
        progressBarMessage: DEFAULT_MESSAGE,
        progressQueueEndpoint: "/api/progress/watch",
        processGUID: "",
        processStatus: null,
        processErrorMessage: "",
        processFinished: false,

        // polling
        pollingIntervalMs: DEFAULT_POLL_INTERVAL,
        pollingTimerId: null,
        pollingInProgress: false,

        // tasks list
        tasksPage: {
            content: [],
            totalElements: 0,
            totalPages: 0,
            size: 20,
            number: 0,
            first: true,
            last: true,
            empty: true,
        },

        // optional info for future toast / notification
        lastCompletedTask: null,
    }),

    getters: {
        getDisplayLazyLoader: (state) => state.displayLazyLoader,
        getDisplayProgressBar: (state) => state.displayProgressBar,
        getProgressBarProgress: (state) => state.progressBarProgress,
        getProgressBarMessage: (state) => state.progressBarMessage,
        getProcessGUID: (state) => state.processGUID,
        getProcessStatus: (state) => state.processStatus,
        getProcessErrorMessage: (state) => state.processErrorMessage,
        getProcessFinished: (state) => state.processFinished,
        getTasksPage: (state) => state.tasksPage,
        getTasksList: (state) => state.tasksPage?.content || [],
        isTrackingProcess: (state) => !!state.processGUID,
    },

    actions: {
        resetProgressState() {
            this.progressBarProgress = 0;
            this.progressBarMessage = DEFAULT_MESSAGE;
            this.processStatus = null;
            this.processErrorMessage = "";
            this.processFinished = false;
        },

        setDisplayLazyLoader(displayLazyLoader) {
            this.displayLazyLoader = displayLazyLoader;

            if (displayLazyLoader === false) {
                this.displayProgressBar = false;
            }
        },

        setDisplayProgressBar(displayProgressBar) {
            this.displayProgressBar = displayProgressBar;

            if (displayProgressBar === false) {
                this.displayLazyLoader = false;
            } else {
                this.displayLazyLoader = true;
            }
        },

        setProgressBarProgress(progressBarProgress) {
            const parsed = Number(progressBarProgress);
            this.progressBarProgress = Number.isFinite(parsed) ? parsed : 0;
        },

        setProgressBarMessage(progressBarMessage) {
            this.progressBarMessage = progressBarMessage || DEFAULT_MESSAGE;
        },

        setProcessGUID(processGUID) {
            this.processGUID = processGUID || "";
        },

        setProcessStatus(processStatus) {
            this.processStatus = processStatus || null;
        },

        setProcessErrorMessage(processErrorMessage) {
            this.processErrorMessage = processErrorMessage || "";
        },

        /**
         * Call this after backend returns taskId.
         * Starts tracking and opens modal progress bar.
         */
        startTrackingProcess(taskId,responseMessage, options = {},onFinishedCallback = () => {}) {
            if (!taskId || taskId.length === 0) {
                return;
            }
            if(responseMessage === undefined || responseMessage === null || responseMessage === ""){
                responseMessage = "Zadanie zostało dodane do kolejki...";
            }
            const {
                message = responseMessage,
                showModal = true,
                showProgressBar = true,
                resetState = true,
            } = options;

            if (resetState) {
                this.resetProgressState();
            }

            this.setProcessGUID(taskId);
            this.setProgressBarMessage(message);

            if (showModal) {
                this.setDisplayLazyLoader(true);
            }
            if (showProgressBar) {
                this.setDisplayProgressBar(true);
            }

            this.startProcessPolling(onFinishedCallback);
        },

        /**
         * Only closes modal. Does NOT stop backend task tracking.
         */
        closeProgressBar() {
            this.setDisplayProgressBar(false);
            this.setDisplayLazyLoader(false);
        },

        /**
         * Fully stops observing current task on frontend.
         * Backend task continues anyway.
         */
        stopTrackingProcess() {
            this.stopProcessPolling();
            this.setProcessGUID("");
        },

        startProcessPolling(onFinishedCallback = () => {}) {
            if (!this.processGUID) {
                return;
            }

            this.stopProcessPolling();

            this.fetchCurrentProcessStatus(onFinishedCallback);

            this.pollingTimerId = window.setInterval(() => {
                this.fetchCurrentProcessStatus(onFinishedCallback);
            }, this.pollingIntervalMs);
        },

        stopProcessPolling() {
            if (this.pollingTimerId) {
                window.clearInterval(this.pollingTimerId);
                this.pollingTimerId = null;
            }
        },

        async fetchCurrentProcessStatus(onFinishedCallback = () => {}) {
            if (!this.processGUID || this.pollingInProgress) {
                return;
            }

            this.pollingInProgress = true;

            try {
                const response = await this.httpGetProcessProgressStatus({
                    taskId: this.processGUID,
                });

                const payload = response?.data?.payload;
                if (!payload) {
                    return;
                }

                const normalizedProgress = this.normalizeProgress(payload.progress);
                this.setProgressBarProgress(normalizedProgress);
                this.setProgressBarMessage(payload.message || DEFAULT_MESSAGE);
                this.setProcessStatus(payload.status || null);
                this.setProcessErrorMessage(payload.errorMessage || "");

                const isFinished = this.isTerminalStatus(payload.status);

                if (isFinished) {
                    this.processFinished = true;
                    this.stopProcessPolling();

                    if (payload.status === "DONE") {
                        if(this.getDisplayProgressBar || this.getDisplayLazyLoader){
                            await apiRequestsHelper.handleProgressResponseWithDialogs(response, true);
                        }
                        this.setProgressBarProgress(100);
                        this.setProgressBarMessage(payload.message || "Zakończono");
                        this.setDisplayLazyLoader(false);
                        this.setDisplayProgressBar(false);
                    }

                    if (payload.status === "FAILED") {
                        this.setProgressBarMessage(
                            payload.errorMessage || payload.message || "Zadanie zakończyło się błędem."
                        );
                    }

                    this.lastCompletedTask = {
                        taskId: this.processGUID,
                        status: payload.status,
                        message: payload.message || "",
                        errorMessage: payload.errorMessage || "",
                        finishedAt: payload.finishedAt || null,
                    };

                    onFinishedCallback();
                }
            } catch (error) {
                log.log("Wystąpił błąd podczas pobierania statusu procesu: " + error);

                /**
                 * Nie kończymy od razu obserwacji przy pojedynczym błędzie HTTP,
                 * żeby nie ubić monitoringu chwilowym problemem sieciowym.
                 */
            } finally {
                this.pollingInProgress = false;
            }
        },

        normalizeProgress(progressValue) {
            const parsed = Number(progressValue);

            if (!Number.isFinite(parsed)) {
                return 0;
            }

            /**
             * Backend u Ciebie historycznie zwracał progress jako 0.0 - 1.0,
             * więc tu mapujemy to na 0 - 100 dla komponentu progress bara.
             */
            if (parsed <= 1) {
                return Math.max(0, Math.min(100, parsed * 100));
            }

            return Math.max(0, Math.min(100, parsed));
        },

        isTerminalStatus(status) {
            return status === "DONE" || status === "FAILED";
        },

        async httpGetProcessProgressStatus(payload) {
            const endpoint = `${this.progressQueueEndpoint}/id/${payload.taskId}`;

            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    appToken: "abc",
                },
            });
        },

        async httpGetProgressTasksPage(payload = {}) {
            const page = payload.page ?? 0;
            const size = payload.size ?? 20;
            const endpoint = `${this.progressQueueEndpoint}/list?page=${page}&size=${size}`;

            const response = await axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    appToken: "abc",
                },
            });

            if (response.status === 200 && response.data?.payload) {
                this.tasksPage = response.data.payload;
            }

            return response;
        },

        /**
         * Helper to use in places where backend returns taskId in payload.
         */
        handleQueuedTaskResponse(response, options = {},onFinishedCallback = () => {}) {
            const taskId = response?.data?.payload;
            const message = response?.data?.message;
            console.log("handleQueuedTaskResponse", response, taskId, message);
            if (taskId && typeof taskId === "string") {
                this.startTrackingProcess(taskId,message, options,onFinishedCallback);
            }
            return taskId;
        },
    },
});